/**
 * Tax & GST classification for a single client.
 *
 * Two fields feed the portal home hero cards:
 *   - `entity_type`  → drives the deterministic Tax Reserve estimate.
 *   - `gst_registered` + `gst_filing_frequency` → drive the GST Position
 *     estimate + next-return due date on the portal.
 *
 * Numbers are computed by the backend snapshot endpoint from raw
 * Xero/Prosaic figures — this card only exposes the classification
 * levers, never a money edit.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Landmark, Info } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INDUSTRY_SUGGESTIONS = [
    "Cafe / hospitality",
    "Construction / builder",
    "Plumbing",
    "Electrical",
    "Painter / decorator",
    "Retail — general",
    "E-commerce / online retail",
    "Rideshare / delivery",
    "Beauty / hair salon",
    "Personal trainer / fitness",
    "Graphic designer",
    "IT / software consultant",
    "Accounting / bookkeeping",
    "Legal services",
    "Property investment / rental",
    "Farming / agriculture",
    "Cleaning services",
    "Freight / transport",
    "Marketing / advertising",
    "Healthcare / medical",
];

const ENTITY_OPTIONS = [
    { value: "company",     label: "Company (28%)" },
    { value: "sole_trader", label: "Sole trader (progressive up to 33%)" },
    { value: "trust",       label: "Trust (33%)" },
    { value: "partnership", label: "Partnership (owners tax up to 33%)" },
    { value: "individual",  label: "Individual (33%)" },
];

const FREQ_OPTIONS = [
    { value: "2-monthly", label: "2-monthly" },
    { value: "6-monthly", label: "6-monthly" },
    { value: "annual",    label: "Annual" },
];

export default function TaxGstSettingsCard({ client, onSaved }) {
    const [form, setForm] = useState({
        entity_type: client?.entity_type || "",
        industry: client?.industry || "",
        gst_registered: !!client?.gst_registered,
        gst_filing_frequency: client?.gst_filing_frequency || "2-monthly",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm({
            entity_type: client?.entity_type || "",
            industry: client?.industry || "",
            gst_registered: !!client?.gst_registered,
            gst_filing_frequency: client?.gst_filing_frequency || "2-monthly",
        });
    }, [client?.id, client?.entity_type, client?.industry, client?.gst_registered, client?.gst_filing_frequency]);

    const dirty = (
        (form.entity_type || null) !== (client?.entity_type || null) ||
        (form.industry || "").trim() !== (client?.industry || "").trim() ||
        form.gst_registered !== !!client?.gst_registered ||
        (form.gst_filing_frequency || "2-monthly") !== (client?.gst_filing_frequency || "2-monthly")
    );

    async function save() {
        setBusy(true);
        try {
            const payload = {
                entity_type: form.entity_type || null,
                industry: (form.industry || "").trim() || null,
                gst_registered: form.gst_registered,
                gst_filing_frequency: form.gst_filing_frequency,
            };
            await api.patch(`/clients/${client.id}`, payload);
            toast.success("Tax & GST settings saved — the portal home cards will refresh now.");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="co-card co-card-pad" data-testid="tax-gst-settings-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="co-label flex items-center gap-1.5">
                        <Landmark className="h-3.5 w-3.5" /> Tax &amp; GST classification
                    </div>
                    <h3 className="mt-1 font-display text-lg">How this client shows up on the portal home</h3>
                    <p className="mt-1 text-sm text-[#5E636E] max-w-2xl">
                        Sets the deterministic estimates for <strong>Tax Reserve</strong> and
                        {" "}<strong>GST Position</strong>. Once you approve a formal tax summary these
                        estimates are replaced with the reviewed figure automatically.
                    </p>
                </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                    <Label className="co-label" htmlFor="industry-input">Industry / trade</Label>
                    <Input
                        id="industry-input"
                        list="industry-suggestions"
                        placeholder="e.g. Plumbing, Cafe / hospitality, Graphic designer"
                        value={form.industry}
                        onChange={(e) => setForm({ ...form, industry: e.target.value })}
                        className="mt-1"
                        data-testid="industry-input"
                    />
                    <datalist id="industry-suggestions">
                        {INDUSTRY_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                    </datalist>
                    <p className="mt-1 text-[11px] text-[#5E636E]">
                        The AI &quot;Can I claim it?&quot; helper reads this to tailor deductibility answers to the nature of the trade
                        — a laptop for a graphic designer is 100% claimable; the same purchase for a plumber may not be.
                    </p>
                </div>

                <div>
                    <Label className="co-label">Entity type</Label>
                    <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                        <SelectTrigger className="mt-1" data-testid="entity-type-select">
                            <SelectValue placeholder="— Choose entity type —" />
                        </SelectTrigger>
                        <SelectContent>
                            {ENTITY_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value} data-testid={`entity-option-${o.value}`}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] text-[#5E636E]">
                        Drives the flat tax rate used for the estimate. Company &amp; trust are flat; sole trader is
                        approximated to 33% (conservative).
                    </p>
                </div>

                <div>
                    <Label className="co-label">GST</Label>
                    <label className="mt-1 flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            className="h-4 w-4 accent-emerald-600"
                            checked={form.gst_registered}
                            onChange={(e) => setForm({ ...form, gst_registered: e.target.checked })}
                            data-testid="gst-registered-toggle"
                        />
                        <span>Registered for GST</span>
                    </label>
                    <div className="mt-2">
                        <Label className="text-[11px] text-[#5E636E]">Return frequency</Label>
                        <Select
                            value={form.gst_filing_frequency}
                            onValueChange={(v) => setForm({ ...form, gst_filing_frequency: v })}
                            disabled={!form.gst_registered}
                        >
                            <SelectTrigger className="mt-1" data-testid="gst-frequency-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FREQ_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value} data-testid={`gst-freq-${o.value}`}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-md border border-sky-100 bg-sky-50/60 p-3 text-[12px] text-sky-900">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                    Numbers on the portal come from your latest Xero/Prosaic sync. Tax reserve = net profit × entity rate.
                    GST position = (revenue − expenses) × 3/23 for the current period. Both are labelled
                    <em> Est.</em> on the client&apos;s screen until you approve a formal tax summary.
                </span>
            </div>

            <div className="mt-4 flex items-center gap-2">
                <Button
                    onClick={save}
                    disabled={!dirty || busy}
                    className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                    data-testid="tax-gst-save-btn"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </Button>
                {!dirty && <span className="text-xs text-[#5E636E]">All saved.</span>}
            </div>
        </div>
    );
}
